import API from 'lib/api'
import chunk from 'lodash/chunk'
import moment from 'moment'
import {
  ACCEPTED_IMAGE_TYPES,
  BULK_DELETE_UPLOAD_FIELD,
  HERO_IMAGE_SIDE,
  MAX_FILE_SIZE_BYTES,
  MAX_IMAGE_SIDE,
  MEDIA_AUDIT_LOGS_QUERY_API_URL,
  MEDIA_BULK_DELETE_API_URL,
  MEDIA_BULK_DELETE_PREVIEW_API_URL,
  MEDIA_UPLOAD_API_URL,
  MEDIA_UPLOAD_BATCH_SIZE,
  MEDIA_UPLOAD_MAX_RETRY_ATTEMPTS,
  MIN_IMAGE_SIDE,
  PRODUCT_MEDIA_FILENAME_PATTERN,
} from './constants'
import { normalizeMediaAuditLogItem } from './MediaAuditLogs/auditLogFields'

export function validateProductMediaFile(file) {
  if (!file) {
    return 'No file selected.'
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'File exceeds the 8 MB size limit.'
  }

  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return 'Only JPG and JPEG images are supported.'
  }

  if (!PRODUCT_MEDIA_FILENAME_PATTERN.test(file.name)) {
    return `File name must follow {sku}_{${MIN_IMAGE_SIDE}-${MAX_IMAGE_SIDE}}.jpg, or {sku}_${HERO_IMAGE_SIDE}.jpg for the hero image (e.g. 10000661_1.jpg).`
  }

  return null
}

export function uploadProductMedia(file) {
  const formData = new window.FormData()
  formData.append('type', 'PRODUCT_IMAGE')
  formData.append('file', file, file.name)

  const api = new API({ url: MEDIA_UPLOAD_API_URL })
  return api.post(formData).then(parseMediaUploadResponse)
}

export async function uploadProductMediaInBatches(
  files,
  {
    batchSize = MEDIA_UPLOAD_BATCH_SIZE,
    onFileStatusChange,
    uploadFile = uploadProductMedia,
  } = {}
) {
  const batches = chunk(
    files.map((file, index) => ({ file, index })),
    batchSize
  )

  for (const batch of batches) {
    await Promise.all(
      batch.map(async ({ file, index }) => {
        onFileStatusChange?.(index, { status: 'uploading' })

        try {
          const { transformedImageUrl, message } = await uploadFile(file)
          onFileStatusChange?.(index, {
            status: 'success',
            transformedImageUrl,
            message,
          })
        } catch (error) {
          onFileStatusChange?.(index, { status: 'error', error })
        }
      })
    )
  }
}

function parseMediaUploadResponse(response) {
  const code = response?.code
  if (code != null && code !== 200) {
    const error = new Error(response.message || 'Upload failed.')
    error.code = code
    throw error
  }

  const payload = response?.data || {}

  return {
    transformedImageUrl: payload.transformedImageUrl || '',
    message: response?.message || '',
  }
}

export function getUploadResponseCode(error) {
  if (typeof error?.code === 'number') {
    return error.code
  }

  const dataCode = error?.response?.data?.code
  if (typeof dataCode === 'number') {
    return dataCode
  }

  const status = error?.response?.status
  if (typeof status === 'number') {
    return status
  }

  return null
}

/** Retry only when the API did not return business code 200 or 400. */
export function isRetryableUploadFailure(error) {
  const code = getUploadResponseCode(error)
  return code !== 200 && code !== 400
}

export function isUploadRetryAllowed(
  retryCount,
  maxRetries = MEDIA_UPLOAD_MAX_RETRY_ATTEMPTS
) {
  return retryCount < maxRetries
}

const GENERIC_API_ERROR_MESSAGE = 'An error has occured'

function extractServiceErrorMessage(error, fallbackMessage) {
  if (typeof error?.response?.data?.error === 'string') {
    return error.response.data.error
  }

  if (typeof error?.response?.data?.message === 'string') {
    return error.response.data.message
  }

  if (error?.message && error.message !== GENERIC_API_ERROR_MESSAGE) {
    return error.message
  }

  return fallbackMessage
}

export function getUploadErrorMessage(error) {
  return extractServiceErrorMessage(error, 'Upload failed.')
}

function buildBulkDeleteFormData(file) {
  const formData = new window.FormData()
  formData.append(BULK_DELETE_UPLOAD_FIELD, file)
  return formData
}

export function previewBulkDelete(file) {
  const api = new API({ url: MEDIA_BULK_DELETE_PREVIEW_API_URL })
  return api
    .post(buildBulkDeleteFormData(file))
    .then(parseBulkDeletePreviewResponse)
}

export function submitBulkDelete(file) {
  const api = new API({ url: MEDIA_BULK_DELETE_API_URL })
  return api.post(buildBulkDeleteFormData(file)).then(parseBulkDeleteResponse)
}

function parseBulkDeleteResponse(response) {
  if (response?.code && response.code !== 200) {
    throw new Error(response.message || 'Delete failed.')
  }

  return response
}

export function parseBulkDeletePreviewResponse(response) {
  if (response?.code && response.code !== 200) {
    throw new Error(response.message || 'Preview failed.')
  }

  const payload = response?.data ?? response
  const items = payload?.items ?? payload?.preview ?? payload?.data ?? []

  return {
    items: Array.isArray(items)
      ? items.map(normalizeBulkDeletePreviewItem)
      : [],
    totalCount:
      payload?.totalCount ??
      payload?.total ??
      (Array.isArray(items) ? items.length : 0),
  }
}

function extractImageSideCode(item = {}) {
  if (Array.isArray(item.sides)) {
    return item.sides.map(String).join(',')
  }

  if (Array.isArray(item.image_side_codes)) {
    return item.image_side_codes.map(String).join(',')
  }

  const code =
    item.imageSideCode ??
    item.image_side_code ??
    item.side ??
    item.imageSide ??
    ''

  return String(code).trim()
}

export function normalizeBulkDeletePreviewItem(item = {}) {
  const imageSideCode = extractImageSideCode(item)

  return {
    sku: String(item.sku ?? item.SKU ?? '').trim(),
    imageSideCode,
    sideLabel: formatImageSideLabel(imageSideCode),
  }
}

const SIDE_LABELS = {
  1: '1 (Front)',
  2: '2 (Left)',
  3: '3 (Right)',
  4: '4 (Back)',
  89: '89 (Hero)',
  xl1: '1 (Front)',
  lxl1: '2 (Left)',
  rxl1: '3 (Right)',
  bxl1: '4 (Back)',
  bxl99: '89 (Hero)',
  hero: '89 (Hero)',
  front: 'Front',
  back: 'Back',
  left: 'Left',
  right: 'Right',
  all: 'All',
}

export function formatImageSideLabel(sideCode) {
  const raw = String(sideCode ?? '').trim()
  if (!raw) {
    return '-'
  }

  const normalized = raw.toLowerCase()
  if (normalized.includes(',')) {
    return normalized
      .split(',')
      .map((part) => part.trim())
      .map((part) => SIDE_LABELS[part] || part)
      .join(', ')
  }

  return SIDE_LABELS[normalized] || raw
}

export function getBulkDeleteErrorMessage(error) {
  return extractServiceErrorMessage(error, 'Request failed.')
}

export function getMediaAuditErrorMessage(error) {
  return extractServiceErrorMessage(error, 'Request failed.')
}

function toApiDateTime(dateValue, endOfDay = false) {
  if (!dateValue) {
    return undefined
  }

  const parsed = moment(dateValue, ['YYYY-MM-DD', moment.ISO_8601], true)
  if (!parsed.isValid()) {
    return undefined
  }

  const value = endOfDay
    ? parsed.endOf('day').utc()
    : parsed.startOf('day').utc()

  return value.format('YYYY-MM-DD[T]HH:mm:ss[Z]')
}

export function buildMediaAuditQueryPayload(filters = {}, pagination = {}) {
  const payload = {
    type: filters.type || 'PRODUCT_IMAGE',
    limit: pagination.limit ?? 20,
    offset: pagination.offset ?? 0,
  }

  const sku = String(filters.sku ?? '').trim()
  if (sku) {
    const numericSku = Number(sku)
    if (!Number.isNaN(numericSku)) {
      payload.sku = numericSku
    }
  }

  const userId = String(filters.userId ?? filters.createdBy ?? '').trim()
  if (userId) {
    const numericUserId = Number(userId)
    if (!Number.isNaN(numericUserId)) {
      payload.createdBy = numericUserId
    }
  }

  const status = String(filters.status ?? '').trim()
  if (status) {
    payload.status = status.toUpperCase()
  }

  const action = String(filters.action ?? '').trim()
  if (action) {
    payload.action = action.toUpperCase()
  }

  const createdFrom = toApiDateTime(filters.createdFrom ?? filters.from, false)
  if (createdFrom) {
    payload.createdFrom = createdFrom
  }

  const createdTo = toApiDateTime(filters.createdTo ?? filters.to, true)
  if (createdTo) {
    payload.createdTo = createdTo
  }

  return payload
}

function parseMediaApiResponse(response, fallbackMessage) {
  if (response?.code != null && response.code !== 200) {
    throw new Error(response.message || fallbackMessage)
  }

  return response
}

export function parseMediaAuditQueryResponse(response) {
  parseMediaApiResponse(response, 'Query failed.')

  const payload = response?.data ?? response
  const rawItems =
    payload?.items ?? payload?.data ?? payload?.auditLogs ?? payload?.logs ?? []

  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeMediaAuditLogItem)
    : []

  const totalCount =
    payload?.totalCount ??
    payload?.total ??
    payload?.count ??
    (Array.isArray(rawItems) ? rawItems.length : 0)

  return {
    items,
    totalCount,
  }
}

export function queryMediaAuditLogs(filters = {}, pagination = {}) {
  const api = new API({ url: MEDIA_AUDIT_LOGS_QUERY_API_URL })
  const payload = buildMediaAuditQueryPayload(filters, pagination)

  return api.post(payload).then(parseMediaAuditQueryResponse)
}
